import React, { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function TeamManagement() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [teams, setTeams] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [newTeam, setNewTeam] = useState({ teamName: '', TeamID: '', competitionId: '', members: [] });
    const [newMemberId, setNewMemberId] = useState('');
    const [teamMembers, setTeamMembers] = useState({});
    const [validTeamMembers, setValidTeamMembers] = useState([]); // Ensure it's initialized as an empty array
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchTeams = useCallback(async () => {
        if (!selectedEventID) {
            console.error('Error: EventID is missing in fetchTeams call.');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/getTeams?EventID=${selectedEventID}`);
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(errorData || 'Failed to fetch teams.');
            }

            const data = await response.json();
            console.log('Fetched teams:', data);
            setTeams(data);
        } catch (error) {
            console.error(`Error fetching teams: ${error.message}`);
            setTeams([]); // Reset teams state on error
        }
    }, [selectedEventID]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                console.log('Fetched events:', data);
                const enabledEvents = data.filter(event => event.Enabled);
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) setSelectedEventID(enabledEvents[0].EventID);
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (!selectedEventID) return;
        const fetchCompetitions = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${selectedEventID}`);
                const data = await response.json();
                console.log('Fetched competitions:', data);
                setCompetitions(data.filter(competition => competition.TeamBased === true)); // Only team-based competitions
            } catch (error) {
                console.error('Error fetching competitions:', error);
                setCompetitions([]); // Reset competitions state on error
            }
        };
        const fetchAllCompetitors = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllCompetitors?EventID=${selectedEventID}`);
                const data = await response.json();
                console.log('Fetched valid team members:', data);
                if (Array.isArray(data)) { // Ensure the response is an array
                    setValidTeamMembers(data);
                } else {
                    console.error('Error: Expected an array of valid team members.');
                    setValidTeamMembers([]); // Reset to an empty array on error
                }
            } catch (error) {
                console.error('Error fetching valid team members:', error);
                setValidTeamMembers([]); // Reset to an empty array on error
            }
        };
        setTeams([]); // Reset teams state when selectedEventID changes
        fetchTeams();
        fetchCompetitions();
        fetchAllCompetitors();
     }, [selectedEventID, fetchTeams]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewTeam({ teamName: '', TeamID: '', competitionId: '', members: [] });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTeam(prev => ({ ...prev, [name]: value }));
    };

    const handleAddTeam = async () => {
        if (!selectedEventID) {
            alert('Please select an event first.');
            return;
        }
        if (!newTeam.teamName.trim() || !newTeam.competitionId.trim()) {
            alert('Please fill in all required fields.');
            return;
        }

        // Check for duplicate team name
        const duplicateTeam = teams.find(team => team.TeamName.toLowerCase() === newTeam.teamName.toLowerCase() && team.CompetitionID === newTeam.competitionId);
        if (duplicateTeam) {
            alert('A team with this name already exists in the selected competition.');
            return;
        }

        const endpoint = `${API_BASE_URL}/createTeam`;
        const method = 'POST';

        try {
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ TeamName: newTeam.teamName, CompetitionID: newTeam.competitionId })
            });

            const responseData = await response.json();
            if (response.ok) {
                const refreshedTeams = await fetch(`${API_BASE_URL}/getTeams?EventID=${selectedEventID}`).then(res => res.json());
                console.log('Refreshed teams:', refreshedTeams);
                setTeams(refreshedTeams);
                closeModal();
                fetchTeams();
            } else {
                alert(`Error: ${responseData.message || 'Failed to add team.'}`);
            }
        } catch (error) {
            console.error('Error adding team:', error);
        }
    };

    const handleCancel = () => {
        closeModal();
    };

    const handleDeleteTeam = async (TeamID, competitionId, TeamName) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this team?');
        if (!confirmDelete) return;

        try {
            const response = await fetch(`${API_BASE_URL}/deleteTeam?TeamID=${TeamID}&TeamName=${encodeURIComponent(TeamName)}&CompetitionID=${competitionId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                setTeams(teams.filter(team => team.TeamID !== TeamID));
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to delete team.'}`);
            }
        } catch (error) {
            console.error('Error deleting team:', error);
        }
    };

    const fetchTeamMembers = async (TeamID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getTeamMembers?TeamID=${TeamID}`);
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(errorData || 'Failed to fetch team members.');
            }
            const data = await response.json();
            setTeamMembers(prev => ({ ...prev, [TeamID]: data.map(member => ({
                CompetitorID: member.CompetitorID,
                FirstName: member.FirstName,
                LastName: member.LastName,
                Organization: member.Organization,
                Division: member.Division,
                CompetitionID: member.CompetitionID,
                CompetitionName: member.CompetitionName,
                EventID: member.EventID,
                EventName: member.EventName
            })) }));
        } catch (error) {
            console.error(`Error fetching team members: ${error.message}`);
            setTeamMembers(prev => ({ ...prev, [TeamID]: [] }));
        }
    };

    const handleAddTeamMember = async () => {
        if (!newMemberId.trim() || !newTeam.TeamID.trim()) {
            alert('Please select a valid team and team member.');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/addTeamMember`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ TeamID: newTeam.TeamID, CompetitorID: newMemberId, EventID: selectedEventID }) // Include EventID
            });

            if (response.ok) {
                fetchTeamMembers(newTeam.TeamID); // Refresh team members
                setNewMemberId(''); // Clear input field
            } else {
                const errorData = await response.json();
                // Check for both error and message properties to handle different API response formats
                alert(`Error: ${errorData.error || errorData.message || 'Failed to add team member.'}`);
            }
        } catch (error) {
            console.error('Error adding team member:', error);
        }
    };

    const handleDeleteTeamMember = async (TeamID, memberId, TeamName, CompetitionID) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this team member?');
        try {
            const response = await fetch(`${API_BASE_URL}/deleteTeamMember?TeamID=${TeamID}&CompetitorID=${memberId}&EventID=${selectedEventID}&TeamName=${encodeURIComponent(TeamName)}&CompetitionID=${CompetitionID}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                fetchTeamMembers(TeamID); // Refresh team members
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to delete team member.'}`);
            }
        } catch (error) {
            console.error('Error deleting team member:', error);
        }
    };

    useEffect(() => {
        teams.forEach(team => fetchTeamMembers(team.TeamID));
    }, [teams]);

    return (
        <div className="p-1">
            <label>
                <select
                    className="form-select"
                    value={selectedEventID}
                    onChange={(e) => setSelectedEventID(e.target.value)}
                >
                    <option value="">Select Event</option>
                    {events.map(event => (
                        <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                    ))}
                </select>
            </label>

            <button className="btn btn-primary mb-1" onClick={openModal}>
                Add Team
            </button>

            <div className="form-section mb-1">
                <h3>Add Team Member</h3>
                <label>
                    <select
                        className="form-select"
                        name="TeamID"
                        value={newTeam.TeamID}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Team</option>
                        {teams.map(team => (
                            <option key={team.TeamID} value={team.TeamID}>{team.CompetitionName} - {team.TeamName}</option>
                        ))}
                    </select>
                </label>
                <label className="mt-1">
                    <select
                        className="form-select"
                        value={newMemberId}
                        onChange={(e) => setNewMemberId(e.target.value)}
                    >
                        <option value="">Select Member</option>
                        {validTeamMembers.map(member => (
                            <option key={member.CompetitorID} value={member.CompetitorID}>
                                {member.CompetitorID} - {member.FirstName} {member.LastName}
                            </option>
                        ))}
                    </select>
                </label>
                <button className="btn btn-primary" onClick={handleAddTeamMember}>Add Member</button>
            </div>

            <table className="data-table">
                <thead>
                    <tr>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {competitions.map(comp => {
                        const compTeams = teams.filter(team => team.CompetitionID === comp.CompetitionID);
                        if (compTeams.length === 0) return null;
                        return (
                            <React.Fragment key={comp.CompetitionID}>
                                <tr>
                                    <td colSpan="2"><h3>{comp.CompetitionName}</h3></td>
                                </tr>
                                {compTeams.map(team => (
                                    <tr key={team.TeamID}>
                                        <td>
                                            <div className="card mb-1">
                                                <div className="flex justify-between gap-1">
                                                    <div>
                                                        <strong>Team Name:</strong> {team.TeamName}
                                                    </div>
                                                </div>
                                                <ul className="team-member-list mt-1">
                                                    {teamMembers[team.TeamID] ? (
                                                        teamMembers[team.TeamID].map(member => (
                                                            <li key={member.CompetitorID} className="team-member-item">
                                                                <button onClick={() => handleDeleteTeamMember(team.TeamID, member.CompetitorID, team.TeamName, team.CompetitionID)} className="btn btn-danger" style={{ marginRight: '0.5rem' }}>X</button>
                                                                <span>{member.CompetitorID} {member.FirstName} {member.LastName} - {member.Organization} - {member.Division}</span>
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li>Loading...</li>
                                                    )}
                                                </ul>
                                                <button className="btn btn-danger" onClick={() => handleDeleteTeam(team.TeamID, team.CompetitionID, team.TeamName)}>Delete Team</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        );
                    })}
                    {teams.length < 1 && (
                        <tr>
                            <td colSpan="2" style={{ textAlign: 'left' }}>No teams found for this event.</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {isModalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Add New Team</h3>
                        <input
                            type="text"
                            name="teamName"
                            className="form-control"
                            placeholder="Team Name (required)"
                            value={newTeam.teamName}
                            onChange={handleInputChange}
                            required
                        />
                        
                        <label>
                            <select
                                className="form-select"
                                name="competitionId"
                                value={newTeam.competitionId}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Competition</option>
                                {Array.isArray(competitions) && competitions.map(competition => (
                                    <option key={competition.CompetitionID} value={competition.CompetitionID}>{competition.CompetitionName}</option>
                                ))}
                            </select>
                        </label>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleAddTeam}>Add Team</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


